import { useStore } from "@/state/legacy_module_api";
import { bootApp } from "./app_bootstrap";
import { bootCore } from "./core_bootstrap";
import { bootInstallGuard } from "./install_guard";
import { bootStorage } from "./storage_bootstrap";
import { bootSession } from "./session_bootstrap";
import { bootRealtime } from "./realtime_bootstrap";
import { bootNotifications } from "./notifications_bootstrap";
import { bootDebug } from "./debug_bootstrap";
import { bootAnalytics } from "./analytics_bootstrap";

/**
 * Platform-specific services container for Web.
 * Web accesses most services through imports, so this is intentionally
 * lightweight. Platform-specific overrides go here when needed.
 */
export interface WebPlatform {
  // Currently empty — web services are accessed via imports
}

/**
 * Canonical bootstrap context — identical field names across all platforms.
 * - session:     token accessor (zustand store on web)
 * - logger:      logging facility (console on web)
 * - appDelegate: null (web has no app delegate)
 * - platform:    platform-specific services
 */
export interface BootstrapContext {
  session: { getToken: () => string | null };
  logger: Console;
  appDelegate: null;
  platform: WebPlatform;
}

let bootstrapContext: BootstrapContext = {
  session: { getToken: () => useStore.getState().apiToken ?? null },
  logger: console,
  appDelegate: null,
  platform: {},
};

export function setBootstrapContext(ctx: Partial<BootstrapContext>): void {
  bootstrapContext = { ...bootstrapContext, ...ctx };
}

let bootstrap_promise: Promise<void> | null = null;
let initialized = false;

export async function runBootstrap(token?: string): Promise<void> {
  if (!bootstrap_promise) {
    bootstrap_promise = (async () => {
      await bootApp();
      await bootCore();
      await bootInstallGuard();
      await bootStorage();
      await bootSession(token);
      await bootRealtime(token);
      await bootNotifications();
      await bootDebug();
      await bootAnalytics();
      initialized = true;
    })();
    await bootstrap_promise;
    return;
  }

  await bootstrap_promise;
  await bootSession(token);
  await bootRealtime(token);
  await bootNotifications();
  await bootDebug();
  await bootAnalytics();
  initialized = true;
}

export async function initializeApp(): Promise<boolean> {
  if (bootstrap_promise && initialized) {
    return !!useStore.getState().apiToken;
  }

  await runBootstrap();
  initialized = true;
  return !!useStore.getState().apiToken;
}

export function isAppInitialized(): boolean {
  return initialized;
}

export function resetInitState(): void {
  initialized = false;
  bootstrap_promise = null;
}

export {
  bootApp,
  bootCore,
  bootStorage,
  bootSession,
  bootRealtime,
  bootNotifications,
  bootDebug,
  bootAnalytics,
};
